import React, {useContext, useState} from "react";
import {injectIntl} from "react-intl";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Select, {SelectOption} from "@amzn/meridian/select";
import {GlobalStateContext} from "../../../global/context/GlobalStateContext";
import {getLocalizedMessage} from "../../../util/Localization";
import {ALERT_UPDATE_SUCCESS_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE, VAPR_MODES} from "../../../util/Constants";
import {updateVaprModeDetails} from "../network/VaprDeviceNetworkAPI";

const CHANGE_MODE_MODAL_WIDTH = 440;

/**
 * "Change Mode" popup for a single VAPR device. Shows the device's current mode and a dropdown of every mode, with
 * the current mode as its placeholder. On Save, an unchanged mode just closes with the success toast (no backend
 * call); a different mode asks for confirmation, saves only desiredMode in place, then shows
 * a success or error toast (with the backend message when present).
 * The network call is made here, not via a reducer case, so the reducer stays pure.
 *
 * @param open        whether the modal is visible
 * @param setOpen     setter to toggle visibility
 * @param mac         MAC of the device being changed
 * @param currentMode the device's normalized mode (VAPR_MODES value)
 * @param dispatch    VAPR overview reducer dispatch (passed to the network layer for the refetch)
 */
const ChangeVaprModeModal = (props) => {
    const {open, setOpen, mac, currentMode, dispatch, intl} = props;
    const {showWarning, showCustomAlert} = useContext(GlobalStateContext);
    const [newMode, setNewMode] = useState("");

    const modeLabel = (modeValue) => {
        const mode = Object.values(VAPR_MODES).find(m => m.value === modeValue);
        return mode ? getLocalizedMessage({intl, stringId: mode.stringId}) : modeValue;
    };
    const onCloseModal = () => {
        setOpen(false);
        setNewMode("");
    };

    const onClickSave = () => {
        if (!newMode || newMode === currentMode) {
            onCloseModal();
            showCustomAlert(ALERT_UPDATE_SUCCESS_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE.SUCCESS);
            return;
        }
        // Changing mode changes how the camera behaves (Boot Mode stops normal projection), so confirm first.
        showWarning(getLocalizedMessage({intl, stringId: "device.editWarning"}), async () => {
            onCloseModal();
            const {isSuccess, errorMessage} = await updateVaprModeDetails(dispatch, newMode, mac);
            showCustomAlert(
                isSuccess ? ALERT_UPDATE_SUCCESS_ASSET_MESSAGE : errorMessage,
                isSuccess ? MERIDIAN_ALERT_TYPE.SUCCESS : MERIDIAN_ALERT_TYPE.ERROR);
        });
    };

    return (
        <Modal
            title={getLocalizedMessage({intl, stringId: "actions.change_mode"})}
            open={open}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            width={CHANGE_MODE_MODAL_WIDTH}
            bodySpacingInset="medium">
            <Column spacing="medium" spacingInset="medium">
                <Column spacing="200">
                    <Text type="b200" color="secondary">
                        {getLocalizedMessage({intl, stringId: "modal.current_mode"})}
                    </Text>
                    <Text type="b300"><b>{modeLabel(currentMode)}</b></Text>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary">
                        {getLocalizedMessage({intl, stringId: "modal.new_mode"})}
                    </Text>
                    <Select
                        value={newMode}
                        onChange={setNewMode}
                        placeholder={modeLabel(currentMode)}
                        width="100%">
                        {Object.values(VAPR_MODES).map(mode =>
                            <SelectOption key={mode.value} value={mode.value}
                                          label={getLocalizedMessage({intl, stringId: mode.stringId})}/>)}
                    </Select>
                </Column>
            </Column>
            <ModalFooter>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" size="medium" onClick={onCloseModal}>
                        {getLocalizedMessage({intl, stringId: "modal.cancel"})}
                    </Button>
                    <Button type="primary" size="medium" onClick={onClickSave}>
                        {getLocalizedMessage({intl, stringId: "button.save"})}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};

export default injectIntl(ChangeVaprModeModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/vaprdevice/modal/ChangeVaprModeModal.js